import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useAuth } from '@/auth/hooks/useAuth';
import { useCompanyAlertCount, useUnseenInvitationCount } from '@/auth/hooks/useInvitationAlerts';
import { AccountAlerts } from '@/auth/components/AccountAlerts';
import {
  usePendingPluginReviewCount,
  PLUGIN_REVIEW_ALERT_ROLES,
} from '@/dev-console/hooks/usePendingPluginReviewCount';
import { IS_COMPANY_PORTAL, shareOrigin } from '@/lib/portal-host';

interface UserMenuProps {
  showAccountLabel?: boolean; // Render an always-expanded "Account" section instead of the avatar dropdown (for the mobile menu)
  onItemSelect?: () => void; // Called when an account item is selected (e.g. to close a containing mobile sheet)
}

export function UserMenu({ showAccountLabel = false, onItemSelect }: UserMenuProps) {
  const { isAuthenticated, user, signOut, isLoading, isDevMode, isGuest } = useAuth();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Pending workspace invitations the user hasn't viewed yet — surfaced as an
  // alert on their name and on the Workspace Controls entry. Cleared when they
  // open Workspace Controls (see useInvitationAlerts / WorkspaceControlsPage).
  const unseenInvites = useUnseenInvitationCount();
  // Company seat invitations not yet seen and "your workspace joined a
  // Company" notices not yet dismissed — shown at the top of this menu
  // (AccountAlerts), where they can be answered or dismissed.
  const companyAlerts = useCompanyAlertCount();
  // Arches awaiting human review — an admin alert surfaced here, on the Dev
  // Console dashboard tile, and on the Plugin Review Queue page.
  const pendingReviews = usePendingPluginReviewCount();

  const isAdmin = useMemo(() => {
    return user?.roles?.includes('admin') ?? false;
  }, [user?.roles]);

  // Support shares the plugin review alert with admin, but not the rest of the
  // Dev Console — so this is a separate gate, not a widened `isAdmin`.
  const canReviewPlugins = useMemo(() => {
    const roles = user?.roles ?? [];
    return PLUGIN_REVIEW_ALERT_ROLES.some((role) => roles.includes(role));
  }, [user?.roles]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return <div className="h-10 w-20 animate-pulse bg-muted rounded-md" />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/auth/login">
          <Button variant="ghost" size="sm">
            Sign in
          </Button>
        </Link>
        <Link to="/auth/signup">
          <Button size="sm">Sign up</Button>
        </Link>
      </div>
    );
  }

  const handleItemSelect = () => {
    setIsOpen(false);
    onItemSelect?.();
  };

  const handleSignOut = async () => {
    handleItemSelect();
    await signOut();
    // On a portal, never the auto-redirecting sign-in (PortalSignedOut says why).
    navigate(IS_COMPANY_PORTAL ? '/auth/signed-out' : '/auth/login');
  };

  const alertCount = unseenInvites + companyAlerts + pendingReviews;

  // Appearance lives here rather than in the header bar: it is a set-once
  // preference, and a permanent icon next to the navigation implied it was a
  // frequently-used control. `next-themes` resolves `theme` asynchronously, so
  // treat anything that isn't explicitly 'dark' as light for the label — an
  // undefined first render must not read as "Light mode" while dark is active.
  const isDark = theme === 'dark';
  const themeItem = (
    <button
      type="button"
      onClick={() => {
        setTheme(isDark ? 'light' : 'dark');
        handleItemSelect();
      }}
      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
    </button>
  );

  // Identity + role badges shown at the top of the account menu (both layouts).
  const accountHeader = (
    <div className="p-3 border-b border-border">
      <p className="text-sm font-medium text-foreground truncate">
        {user?.username || user?.email}
      </p>
      {user?.roles && user.roles.length > 0 && (
        <div className="flex gap-1 mt-1 flex-wrap">
          {user.roles.map((role) => (
            <span
              key={role}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary"
            >
              {role}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // The account links — identical in the desktop avatar dropdown and the
  // always-expanded mobile section.
  const accountItems = (
    <>
      <Link
        to="/contributor/dashboard"
        className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
        onClick={handleItemSelect}
      >
        Arche Dashboard
      </Link>
      <Link
        to="/credits"
        className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
        onClick={handleItemSelect}
      >
        Credits
      </Link>
      <Link
        to="/my-data"
        className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
        onClick={handleItemSelect}
      >
        My Data
      </Link>
      <Link
        to="/workspaces"
        className="flex items-center justify-between w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
        onClick={handleItemSelect}
      >
        <span>Workspace Controls</span>
        <NotificationBadge
          count={unseenInvites}
          aria-label="Pending workspace and Company invitations"
        />
      </Link>
      {/* Company routes require a full account; a guest would only meet a refusal.
          On a Company portal, Company administration happens on fias.io. */}
      {!isGuest && !IS_COMPANY_PORTAL && (
        <Link
          to="/company"
          className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
          onClick={handleItemSelect}
        >
          Company
        </Link>
      )}
      {IS_COMPANY_PORTAL ? (
        // A portal cannot change credentials or account settings; the
        // account is managed on fias.io.
        <a
          href={`${shareOrigin()}/auth/settings`}
          className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
          onClick={handleItemSelect}
        >
          Fias account settings
        </a>
      ) : (
        <Link
          to="/auth/settings"
          className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
          onClick={handleItemSelect}
        >
          Account Settings
        </Link>
      )}
      <Link
        to="/a/arche_customer_support"
        className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
        onClick={handleItemSelect}
      >
        Support
      </Link>
      <div className="border-t border-border my-1" />
      {themeItem}
      {isDevMode && !IS_COMPANY_PORTAL && (
        <>
          <div className="border-t border-border my-1" />
          <Link
            to="/auth/switch-user"
            className="block w-full px-3 py-2 text-sm text-left text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-md"
            onClick={handleItemSelect}
          >
            Switch Dev User
          </Link>
        </>
      )}
      <button
        type="button"
        onClick={handleSignOut}
        className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
      >
        Sign out
      </button>
      {(isAdmin || canReviewPlugins) && !IS_COMPANY_PORTAL && (
        <>
          <div className="border-t border-border my-1" />
          {isAdmin && (
            <Link
              to="/dev-console"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Dev Console
            </Link>
          )}
          {canReviewPlugins && pendingReviews > 0 && (
            <Link
              to="/dev-console/admin/reviews"
              className="flex items-center justify-between w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              <span>Plugin Review Queue</span>
              <NotificationBadge count={pendingReviews} aria-label="Arches awaiting review" />
            </Link>
          )}
        </>
      )}
    </>
  );

  // Mobile menu (inside the Header's Sheet): the account section is always
  // expanded — the containing Sheet already handles open/close, so gating the
  // links behind an extra accordion tap was pure friction.
  if (showAccountLabel) {
    return (
      <div>
        <div className="flex items-center gap-2 px-3 pb-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Account
          </span>
          <NotificationBadge count={alertCount} aria-label="Pending alerts" />
        </div>
        {accountHeader}
        <AccountAlerts />
        <div className="mt-1">{accountItems}</div>
      </div>
    );
  }

  // Desktop: avatar pill toggles a floating dropdown.
  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-sm hover:bg-accent transition-colors"
      >
        <span className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
          {user?.username?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || '?'}
        </span>
        <span className="hidden sm:inline max-w-[150px] truncate text-foreground">
          {user?.username || user?.email || 'User'}
        </span>
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <NotificationBadge
        count={alertCount}
        aria-label="Pending alerts"
        className="pointer-events-none absolute -top-1 -right-1 z-10 ring-2 ring-background"
      />

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md bg-card border border-border shadow-lg z-50">
          {accountHeader}
          <AccountAlerts />
          <div className="p-1">{accountItems}</div>
        </div>
      )}
    </div>
  );
}
